import { useMemo, useState } from 'react'
import { IProductCustomerItem, OrderDetail } from '../../../../../../modules/API/Orders/models/detailsProductCustomer'
import styled from 'styled-components'
import { IPackagesItem } from '../../../../../../modules/API/Defaults/models/packages'
import { IProductListItemNode } from '../../../../../../modules/API/Defaults/models/productListNode'
import useDataFetcher from '../../../knisot/hooks/useFetchData'
import { Defaults } from '../../../../../../modules/API/Defaults/defaults'
import { IVarietyListResponse } from '../../../../../../modules/API/Defaults/models/varietyList'
import ProductModalNestedItem from './ProductModalNestedItem'
import { useTranslation } from 'react-i18next'
import { FormShell, FormGrid, FormField, FormFooter } from '../../../../../../generic/ui/FormLayout'
import ProductForSortingSelector from '../../../../../../generic/ui/Selectors/ProductForSortingSelector'
import VarietySelector from '../../../../../../generic/ui/Selectors/VarietySelector'
import { AccentButton } from '../../../../../../generic/ui/Button/AccentButton'
import { Mono } from '../../../../../../generic/ui/Mono'
import SearchInput from '../../../../../../generic/ui/SearchInput'

type Props = {
  product: IProductCustomerItem
  packages: IPackagesItem[]
  products: IProductListItemNode[]
  handleSaveFunc: (
    OriginalItem: IProductCustomerItem,
    resultItem: IProductCustomerItem
  ) => void
}

const ProductModal = ({
  product,
  packages,
  products,
  handleSaveFunc
}: Props) => {
  const [t] = useTranslation('buttons')
  const [selectedProduct, setSelectedProduct] = useState<number>(product.Product.ProductID)
  const [selectedVariety, setSelectedVariety] = useState<number>(product.Product.VarietyID)
  const [selectedSize] = useState<string>(product.Product.SizeID)
  const [defaultProduct, setDefaultProduct] = useState<IProductCustomerItem>(product)
  const [search, setSearch] = useState<string>('')

  const {
    data: varietyList
  } = useDataFetcher<IVarietyListResponse>(
    Defaults.getVarietyByProductID, `${selectedProduct}`
  )

  const handleChangeItem = (item: OrderDetail) => {
    const newOrderDetails = defaultProduct.OrderDetails.map(e => {
      if (e.RecID === item.RecID) {
        return item
      }
      return e
    })
    setDefaultProduct({
      ...defaultProduct,
      OrderDetails: newOrderDetails
    })
  }

  const total = useMemo(() => {
    return Math.round(defaultProduct.OrderDetails.reduce((acc, item) => {
      return acc + item.OrderPacks
    }, 0))
  }, [defaultProduct])

  const mlai = product.Product.MlaiPacks
  const diff = mlai - total

  const productObj = useMemo(
    () => products.find(p => p.ProductID === selectedProduct) ?? null,
    [products, selectedProduct]
  )
  const varietyObj = useMemo(
    () => varietyList?.variety.find(v => v.VarietyID === selectedVariety) ?? null,
    [varietyList, selectedVariety]
  )
  const hasVarieties = !!varietyList && varietyList.variety.length > 0

  const handleSave = () => {
    // Build the payload from the full working copy, not the search-filtered
    // view — otherwise rows hidden by an active customer search drop from PUT.
    const result = {
      Product: {
        ...product.Product,
        ProductID: selectedProduct,
        VarietyID: selectedVariety,
        SizeID: selectedSize
      },
      OrderDetails: defaultProduct.OrderDetails.map(e => ({
        ...e,
        ProductID: selectedProduct,
        VarietyID: selectedVariety,
        SizeID: selectedSize,
        Unit: 1
      }))
    }
    handleSaveFunc(product, result)
  }

  const renderRows = useMemo(() => {
    return defaultProduct.OrderDetails.filter(item =>
      item.Customer_Name.trim().toLocaleLowerCase().includes(search.toLocaleLowerCase())
    )
  }, [search, defaultProduct])

  return (
    <FormShell>
      <FormGrid>
        <FormField label='פריט' fullWidth={!hasVarieties}>
          <ProductForSortingSelector
            items={products}
            value={productObj}
            onSelect={(p) => {
              setSelectedProduct(p?.ProductID ?? 0)
              setSelectedVariety(0)
            }}
          />
        </FormField>
        {hasVarieties && (
          <FormField label='זן'>
            <VarietySelector
              items={varietyList!.variety}
              value={varietyObj}
              onSelect={(v) => setSelectedVariety(v?.VarietyID ?? 0)}
            />
          </FormField>
        )}
      </FormGrid>

      {/* Summary + customer search stick together while the rows scroll. */}
      <StickyBar>
        <StatStrip>
          <Stat>
            <StatLabel>מלאי</StatLabel>
            <StatValue><Mono>{mlai}</Mono></StatValue>
          </Stat>
          <Stat>
            <StatLabel>סך הכל</StatLabel>
            <StatValue><Mono>{total}</Mono></StatValue>
          </Stat>
          <Stat>
            <StatLabel>הפרש</StatLabel>
            <StatValue $tone={diff < 0 ? 'danger' : diff > 0 ? 'success' : 'ink'}>
              <Mono>{diff}</Mono>
            </StatValue>
          </Stat>
        </StatStrip>
        {defaultProduct.OrderDetails.length > 5 && (
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='חיפוש לקוח'
          />
        )}
      </StickyBar>
      <Rows>
        {renderRows.map((e, i) => (
          <ProductModalNestedItem
            key={e.RecID}
            index={i}
            item={e}
            packages={packages}
            handleChange={handleChangeItem}
            {...e}
          />
        ))}
      </Rows>

      <FormFooter>
        <AccentButton type='button' onClick={handleSave} disabled={!selectedProduct} fullWidth={false}>
          {t('save')}
        </AccentButton>
      </FormFooter>
    </FormShell>
  )
}

export default ProductModal

const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  background: ${({ theme }) => theme.colors.bgLayout};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.radii.md};
  direction: rtl;
`

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`

const StatLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.sm};
  color: ${({ theme }) => theme.colors.ink3};
`

const StatValue = styled.span<{ $tone?: 'danger' | 'success' | 'ink' }>`
  font-size: ${({ theme }) => theme.text.lg};
  font-weight: 600;
  color: ${({ theme, $tone }) =>
    $tone === 'danger'
      ? theme.colors.danger
      : $tone === 'success'
        ? theme.colors.success
        : theme.colors.ink};
`

const StickyBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: sticky;
  top: 0;
  z-index: 50;
  /* Cancel the AppModal Body top padding so the bar pins flush under the
     header instead of leaving a ~1rem gap; restore it as the bar's own
     top padding. */
  margin-top: -1rem;
  padding: 1rem 0 0.5rem;
  background: ${({ theme }) => theme.colors.surface};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-top: -0.875rem;
    padding-top: 0.875rem;
  }
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`
